import { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { MenuManagementSingleSelectInput } from '@/components/templates/PageLayout/Sidebar/MenuManagementsAccordionItem';
import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { nullToEmptyString } from '@/lib/util';
import {
  CategoryFormError,
  validateMemo,
  validateTitleJa,
} from '@/pages/menu-managements/categories/validate';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import {
  getSearchItems,
  SearchItem,
} from '@/pages/menu-managements/services/searchItem';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementCreateBrandCategory } from '@/services/BrandCategory/hooks/create/useMenuManagementCreateBrandCategory';
import { useMenuManagementBrandCategoryShow } from '@/services/BrandCategory/hooks/show/useMenuManagementBrandCategoryShow';
import { useMenuManagementBrandUpdateCategory } from '@/services/BrandCategory/hooks/update/useMenuManagementBrandUpdateCategory';
import { useMenuManagementSearchCategoryItems } from '@/services/BrandItem/hooks/SearchCategoryItems/useMenuManagementSearchCategoryItems';

type Return = {
  associateBrandSelectInput: ReactSingleSelectInput;
  titleJa: string;
  memo: string;
  error: CategoryFormError;
  searchItems: SearchItem[];
  selectedItems: SearchItem[];
  searchItemName: string;
  isSearching: boolean;
  targetsHeight: number;
  initialStringifyFormState: string;
  isLoading: boolean;
  isCategoryItemModalOpen: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreate: () => void;
  handleUpdate: (urlParamsId: string) => void;
  handleInputName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleDeleteDragItem: (itemId: string) => void;
  initializeNewPageState: () => Promise<void>;
  initializeEditPageState: (urlParamsId: string) => Promise<void>;
  handleClickMultipleItemSelect: () => void;
  handleCloseCategoryItemModal: () => void;
  handleSetMultipleItems: (items: SearchItem[]) => void;
};

export const useMenuManagementCategoryForm = (): Return => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [titleJa, setTitleJa] = useState('');
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<CategoryFormError>({
    titleJa: { ...errorObj },
    memo: { ...errorObj },
  });
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  const [memo, setMemo] = useState<string>('');
  const [searchItemName, setSearchItemName] = useState<string>('');
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  const [, setIsDragged] = useState<boolean>(false);
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');
  const [isCategoryItemModalOpen, setIsCategoryItemModalOpen] = useState(false);

  const listUrl = routes.menuManagementCategories.path;

  const handleInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;
    const newError = validateTitleJa(newTitleJa, error);
    setError(newError);
    setTitleJa(newTitleJa);
  };

  const handleInputMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMemo = e.target.value;
    const newError = validateMemo(newMemo, error);
    setError(newError);
    setMemo(newMemo);
  };

  const searchCategoryItems = async (title: string) => {
    try {
      if (brandSelect === null) return;

      setIsSearching(true);
      const items = await useMenuManagementSearchCategoryItems({
        associate_brand_id: brandSelect.value,
        title,
      });
      setSearchItems(items);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClickMultipleItemSelect = () => {
    setIsCategoryItemModalOpen(true);
    searchCategoryItems('');
  };

  const handleSearchItemNameInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputName = e.target.value;
    setSearchItemName(inputName);
    setIsSearching(true);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      await searchCategoryItems(inputName);
    }, 700);
    setTimer(Number(newTimer));
  };

  const handleAddSelectItems = (item: SearchItem) => {
    setSearchItemName('');
    setSelectedItems([item, ...selectedItems]);
  };

  const handleSetMultipleItems = (items: SearchItem[]) => {
    setSelectedItems(items);
  };

  const handleCloseCategoryItemModal = () => {
    setIsCategoryItemModalOpen(false);
  };

  const associateBrandSelectInput: MenuManagementSingleSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (brand: SelectForm | null) => {
      if (brand?.value === brandSelect?.value) return;
      setBrandSelect(brand);
      setSelectedItems([]);
    },
    onReset: () => {
      setBrandSelect({
        value: '',
        label: '',
      });
    },
  };

  const handleOnDragEnd = (result: DropResult) => {
    const [reorderedItem] = selectedItems.splice(result.source.index, 1);
    selectedItems.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setIsDragged((bool) => !bool);
    setSelectedItems(selectedItems);
  };

  const handleDeleteDragItem = (itemId: string) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
    setSelectedItems(newSelectedItems);
  };

  const handleCreate = () => {
    (async () => {
      setIsLoading(true);
      const brand_item_sort_objects = getSearchItems(selectedItems);
      await useMenuManagementCreateBrandCategory({
        associate_brand_id: Number(associateBrandSelectInput.value?.value),
        title: {
          translation: {
            ja: titleJa,
          },
        },
        memo: memo !== '' ? memo : null,
        brand_item_sort_objects,
      });
      setIsLoading(false);
      alert('カテゴリを登録しました。');
      history.push(routes.menuManagementCategories.path);
    })();
  };

  const handleUpdate = (urlParamsId: string) => {
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementBrandUpdateCategory(urlParamsId, {
          associate_brand_id: Number(associateBrandSelectInput.value?.value),
          title: {
            translation: {
              ja: titleJa,
            },
          },
          memo: memo !== '' ? memo : null,
          brand_item_sort_objects: getSearchItems(selectedItems),
        });
        setIsLoading(false);
        alert('カテゴリを更新しました。');
        history.push(listUrl);
      } catch (error) {
        setIsLoading(false);
        if (error instanceof HttpError && error.status === 400) {
          return alert(error.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error });
      }
    })();
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });

    setAssociateBrandSelectOptions(associateBrandOptions);
    setBrandSelect(initialAssociateBrand);
  };

  const initializeEditPageState = async (urlParamsId: string) => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);

    const category = await useMenuManagementBrandCategoryShow(urlParamsId);
    setBrandSelect(associateBrand);
    const titleJa = category.title.translation.ja;
    const memo = nullToEmptyString(category.memo);
    const items = category.brand_item_sort_objects;
    setTitleJa(titleJa);
    setMemo(memo);
    setSelectedItems(items);

    setInitialStringifyFormState(
      JSON.stringify([titleJa, memo, selectedItems])
    ); // WARNING 文字列比較なので順番を変更するときは注意
  };

  return {
    associateBrandSelectInput,
    titleJa,
    memo,
    error,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    initialStringifyFormState,
    isLoading,
    isCategoryItemModalOpen,
    setIsLoading,
    handleCreate,
    handleUpdate,
    handleInputName,
    handleInputMemo,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    initializeNewPageState,
    initializeEditPageState,
    handleClickMultipleItemSelect,
    handleCloseCategoryItemModal,
    handleSetMultipleItems: handleSetMultipleItems,
  };
};
